import { z } from "zod"

import { tenantIdSchema } from "./identifiers.js"

const scopedIdSchema = z.string().min(1).max(140)
const safeTextSchema = z.string().min(1).max(160)
const timestampSchema = z.string().datetime({ offset: true })

export const customerToCashQueueV1Schema = z.enum([
  "stuck_commands",
  "unmatched_provider_events",
  "payment_exceptions",
  "communication_exceptions",
  "invoice_aging",
  "audit_history",
])

export const customerToCashReportRequestV1Schema = z.object({
  reportVersion: z.literal(1),
  queue: customerToCashQueueV1Schema,
  legalEntityId: scopedIdSchema.nullable(),
  branchId: scopedIdSchema.nullable(),
  cursor: z.number().int().min(0).max(10_000),
  pageSize: z.number().int().min(1).max(50),
}).strict()

const reportFilterOptionV1Schema = z.object({
  id: scopedIdSchema,
  label: safeTextSchema,
  legalEntityId: scopedIdSchema.nullable(),
}).strict()

export const customerToCashReportItemV1Schema = z.object({
  itemId: scopedIdSchema,
  kind: customerToCashQueueV1Schema,
  title: safeTextSchema,
  status: safeTextSchema,
  occurredAt: timestampSchema,
  legalEntityId: scopedIdSchema.nullable(),
  branchId: scopedIdSchema.nullable(),
  primaryRef: safeTextSchema.nullable(),
  detailCode: safeTextSchema,
  amountMinor: z.number().int().safe().nonnegative().nullable(),
  currency: z.string().regex(/^[A-Z]{3}$/).nullable(),
}).strict()

export const customerToCashReportV1Schema = z.object({
  reportVersion: z.literal(1),
  tenantId: tenantIdSchema,
  observedAt: timestampSchema,
  filters: z.object({
    legalEntities: z.array(reportFilterOptionV1Schema).max(100),
    branches: z.array(reportFilterOptionV1Schema).max(250),
    selectedLegalEntityId: scopedIdSchema.nullable(),
    selectedBranchId: scopedIdSchema.nullable(),
  }).strict(),
  summary: z.object({
    stuckCommands: z.number().int().nonnegative(),
    unmatchedProviderEvents: z.number().int().nonnegative(),
    paymentExceptions: z.number().int().nonnegative(),
    communicationExceptions: z.number().int().nonnegative(),
    overdueInvoices: z.number().int().nonnegative(),
  }).strict(),
  page: z.object({
    queue: customerToCashQueueV1Schema,
    items: z.array(customerToCashReportItemV1Schema).max(50),
    nextCursor: z.number().int().min(0).max(10_000).nullable(),
    hasMore: z.boolean(),
  }).strict(),
}).strict()

export type CustomerToCashQueueV1 = z.infer<typeof customerToCashQueueV1Schema>
export type CustomerToCashReportItemV1 = z.infer<typeof customerToCashReportItemV1Schema>
export type CustomerToCashReportRequestV1 = z.infer<
  typeof customerToCashReportRequestV1Schema
>
export type CustomerToCashReportV1 = z.infer<typeof customerToCashReportV1Schema>
